import moment from 'moment'
import { plannedShippingDate } from '../../../../../utils/plannedShippingDate'
import { toCustomerReferences } from '../../../../../utils/productIdentifiers'
import { senderIdentityPayload } from '../../../../../utils/senderIdentity'
import {
  actualKg,
  addressReady,
  blankForm,
  blankItem,
  cleanPhone,
  formKind,
  itemsValue,
  num,
  ownerSide,
  resolveParty,
  senderForId,
} from './rules'
import type { BookingForm, Country, Customer, Item, Kind, Party, Price, Who } from './types'

/*
 * Everything sent to the backend is built here, in the exact shapes the old
 * Export/Import/Domestic pages sent — the redesign changes the form, not the API.
 */

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '')

export const countryName = (countries: Country[], code: string) =>
  countries.find((c) => c.isoCode === code)?.name || code

export const dialCode = (countries: Country[], code: string) => {
  const raw = countries.find((c) => c.isoCode === code)?.phoneCode || (code === 'NG' ? '234' : '')
  const digits = String(raw).replace(/\D/g, '')
  return digits ? `+${digits}` : ''
}

const packagesOf = (f: BookingForm) =>
  f.parcels.map((p) => ({
    weight: num(p.w),
    dimensions: { length: num(p.l), width: num(p.wd), height: num(p.h) },
  }))

const addOnsOf = (f: BookingForm) => [
  ...(f.extras.phyto ? [{ name: 'Phytosanitary Certificate', quantity: 1 }] : []),
  ...(f.extras.bubble > 0 ? [{ name: 'Bubble wrap', quantity: f.extras.bubble }] : []),
  // Spelling matches the add-on name the backend prices
  ...(f.extras.vacuum > 0 ? [{ name: 'Vaccuum seal', quantity: f.extras.vacuum }] : []),
]

/** Documents carry a nominal value of 1, as before. */
const goodsValue = (f: BookingForm) => (f.type === 'document' ? 1 : num(f.value))

const shippingDate = (f: BookingForm) =>
  f.handover.mode === 'pickup' && f.handover.date
    ? plannedShippingDate(f.handover.date)
    : plannedShippingDate()

/**
 * countyName differs by route, exactly as the old forms sent it: domestic
 * prices by state; on export only the receiver's state is sent; elsewhere the
 * country name goes in its place.
 */
const priceAddress = (f: BookingForm, who: Who, p: Party, countries: Country[]) => {
  const k = formKind(f) as Kind
  const cc = who === 'sender' ? f.from : f.to
  let countyName = countryName(countries, cc)
  if (k === 'domestic') countyName = who === 'sender' ? f.fromState : f.toState
  else if (k === 'export' && who === 'receiver') countyName = p.addr.state
  return {
    postalCode: p.addr.pc,
    cityName: cap(p.addr.city),
    countryCode: cc,
    addressLine1: p.addr.l1,
    countyName,
  }
}

/**
 * price/customer-quote — the live estimate. Real addresses are only sent once
 * both ends are complete: a real address on one side and the backend's
 * placeholder on the other can be rejected by DHL. Until then the route alone
 * is priced (plus the states for domestic, which are zoned by state).
 */
export function buildQuoteBody(f: BookingForm, customer: Customer | null) {
  const k = formKind(f)
  const exact = addressReady(f, 'sender', customer) && addressReady(f, 'receiver', customer)
  const end = (who: Who) => {
    const p = resolveParty(f, who, customer)
    const out: Record<string, string> = { countryCode: who === 'sender' ? f.from : f.to }
    if (k === 'domestic') out.countyName = who === 'sender' ? f.fromState : f.toState
    if (!exact) return out
    if (k !== 'domestic' && p.addr.state) out.countyName = p.addr.state
    out.cityName = cap(p.addr.city)
    out.postalCode = p.addr.pc
    out.addressLine1 = p.addr.l1
    return out
  }
  return {
    ...(customer ? { userId: customer.value } : {}),
    deliveryType: k,
    shipmentType: f.type,
    packages: f.parcels.map((p) => ({
      weight: num(p.w),
      length: num(p.l),
      width: num(p.wd),
      height: num(p.h),
    })),
    fromAddress: end('sender'),
    toAddress: end('receiver'),
    insurance: f.extras.protect,
    declaredValue: goodsValue(f),
    currency: 'NGN',
    addOns: addOnsOf(f),
    plannedShippingDateAndTime: shippingDate(f),
  }
}

/** price/admin-single — the final price the booking is charged. */
export function buildPriceData(f: BookingForm, customer: Customer, countries: Country[]) {
  const k = formKind(f) as Kind
  return {
    userId: customer.value,
    document: k !== 'domestic' && f.type === 'document' ? 'document' : 'non_document',
    deliveryType: k,
    customerDetails: {
      shipperDetails: priceAddress(f, 'sender', resolveParty(f, 'sender', customer), countries),
      receiverDetails: priceAddress(f, 'receiver', resolveParty(f, 'receiver', customer), countries),
    },
    plannedShippingDateAndTime: shippingDate(f),
    packages: packagesOf(f),
    insurance: f.extras.protect,
    monetaryAmount: [{ typeCode: 'declaredValue', value: goodsValue(f), currency: 'NGN' }],
    addOns: addOnsOf(f),
  }
}

/* ---------- Items ---------- */

/** Domestic and document shipments have no item step; one line describes the parcel. */
const syntheticItem = (f: BookingForm): Item => ({
  ...blankItem(),
  desc: f.contents.trim(),
  qty: '1',
  price: String(goodsValue(f)),
  weight: String(actualKg(f)),
  code: formKind(f) === 'domestic' ? { value: 'nil', label: 'nil' } : { value: '1234', label: 'document' },
})
const effectiveItems = (f: BookingForm) =>
  formKind(f) !== 'domestic' && f.type === 'package' ? f.items : [syntheticItem(f)]

/** The stored copy on the booking/draft (`specifiedItems`). */
export const storedItems = (f: BookingForm) =>
  effectiveItems(f).map((it) => ({
    itemName: it.desc.trim(),
    description: it.desc.trim(),
    itemQuantity: num(it.qty),
    netValue: num(it.weight),
    grossValue: num(it.weight),
    itemValue: num(it.price),
    commodityCodes: { value: it.code?.value || '', label: it.code?.label || '' },
    ...(formKind(f) === 'export' && it.pid.merchant
      ? { productIdentifiers: { ...it.pid } }
      : {}),
  }))

/** The customs value: the naira goods value, or the item total when prices are in another currency. */
export const customsValue = (f: BookingForm) =>
  f.type === 'package' && f.currency !== 'NGN' ? itemsValue(f) : goodsValue(f)

/* ---------- Booking ---------- */

const contact = (f: BookingForm, who: Who, p: Party, countries: Country[], fallbackEmail = '') => {
  const cc = who === 'sender' ? f.from : f.to
  return {
    phone: `${dialCode(countries, cc)}${cleanPhone(p.phone, cc)}`,
    // The API rejects an empty company; the person's name stands in, as agents did by hand
    companyName: p.company.trim() || `${p.first.trim()} ${p.last.trim()}`,
    fullName: `${p.first.trim()} ${p.last.trim()}`,
    email: p.email.trim() || fallbackEmail,
  }
}

export function buildBookingPayload(args: {
  f: BookingForm
  customer: Customer
  countries: Country[]
  price: Price
  paymentMethod: string
  location: string
  draftId?: string | null
}) {
  const { f, customer, countries, price, paymentMethod, location, draftId } = args
  const k = formKind(f) as Kind
  const sender = resolveParty(f, 'sender', customer)
  const receiver = resolveParty(f, 'receiver', customer)
  const packages = packagesOf(f)
  // The single-package booking carries the charged (volumetric-inclusive) weight
  if (packages.length === 1) packages[0].weight = price.breakDown.grossWeight

  const postal = (who: Who, p: Party) => {
    const a = priceAddress(f, who, p, countries)
    // On export the booking sends the country, not the receiver's state
    if (k === 'export' && who === 'receiver') a.countyName = countryName(countries, f.to)
    const withLine2 = (k === 'export' && who === 'receiver') || k === 'import'
    return withLine2 && p.addr.l2.trim() ? { ...a, addressLine2: p.addr.l2.trim() } : a
  }

  const data: any = {
    type: k.toUpperCase(),
    userId: customer.value,
    paymentMethod,
    charge: Number(price.totalPrice).toFixed(2),
    insurance: f.extras.protect,
    shipmentData: {
      plannedShippingDateAndTime: shippingDate(f),
      pickup: f.handover.mode === 'pickup',
      declaredValue: k === 'domestic' ? goodsValue(f) : customsValue(f),
      ...(k !== 'domestic' ? { declaredValueCurrency: f.type === 'package' ? f.currency : 'NGN' } : {}),
      sender: {
        postalAddress: postal('sender', sender),
        contactInformation: contact(f, 'sender', sender, countries, customer.email),
      },
      receiver: {
        postalAddress: postal('receiver', receiver),
        contactInformation: contact(f, 'receiver', receiver, countries),
      },
      packages,
      description: f.contents.trim(),
    },
    bookingData: {
      category: k === 'domestic' ? 'Products' : 'Eletronics',
      shipment_type: f.type.toUpperCase(),
      value: goodsValue(f),
      ...senderIdentityPayload(senderForId(f, customer), customer as any),
    },
    location,
    specifiedItems: storedItems(f),
    packageImage: f.photo,
    price,
    addOns: addOnsOf(f),
  }

  if (k !== 'domestic') {
    const lineItems = effectiveItems(f).map((it, i) => ({
      number: i + 1,
      quantity: { unitOfMeasurement: 'PCS', value: num(it.qty) },
      price: num(it.price),
      description: it.desc.trim(),
      weight: { netValue: num(it.weight), grossValue: num(it.weight) },
      commodityCodes: [{ typeCode: 'outbound', value: it.code?.value || '' }],
      exportReasonType: 'permanent',
      manufacturerCountry: 'NG',
      // EU product identifiers (ABW / MF / SRV); left off when none
      ...(k === 'export' ? { customerReferences: toCustomerReferences(it.pid) } : {}),
    }))
    data.international = {
      exportDeclaration: {
        lineItems,
        exportReason: f.purpose,
        invoice: {
          number: `invoice number ${Math.random()}`,
          date: moment().format('YYYY-MM-DD'),
          totalNetWeight: actualKg(f),
          totalGrossWeight: price.breakDown.grossWeight,
        },
        placeOfIncoterm: receiver.addr.city,
        exportReasonType: 'permanent',
        shipmentType: 'personal',
      },
    }
  }
  if (draftId) data.draftId = draftId
  return data
}

/* ---------- Drafts (legacy `delivery` shape, shared with the customer apps) ---------- */

export function buildDraftPayload(f: BookingForm, customer: Customer, creatorName: string) {
  const k = formKind(f) as Kind
  const sender = resolveParty(f, 'sender', customer)
  const receiver = resolveParty(f, 'receiver', customer)
  return {
    shipment_type: f.type === 'document' ? 'Document' : 'Package',
    packages: packagesOf(f),
    description: f.contents.trim(),
    value: goodsValue(f),
    purpose: f.purpose,
    country: f.from,
    receiverCountry: f.to,
    sender_first_name: sender.first,
    sender_last_name: sender.last,
    sender_company: sender.company.trim() || `${sender.first} ${sender.last}`.trim(),
    email: sender.email,
    pickup_number: cleanPhone(sender.phone, f.from),
    pickup_address: sender.addr.l1,
    pickup_address2: sender.addr.l2,
    city: sender.addr.city,
    state: k === 'domestic' ? f.fromState : sender.addr.state,
    postcode: sender.addr.pc,
    receiver_first_name: receiver.first,
    receiver_last_name: receiver.last,
    receiver_company: receiver.company.trim() || `${receiver.first} ${receiver.last}`.trim(),
    delivery_email: receiver.email,
    delivery_number: cleanPhone(receiver.phone, f.to),
    delivery_address: receiver.addr.l1,
    delivery_address2: receiver.addr.l2,
    delivery_city: receiver.addr.city,
    delivery_state: k === 'domestic' ? f.toState : receiver.addr.state,
    Deliverypostcode: receiver.addr.pc,
    pickup: f.handover.mode === 'pickup',
    pickup_date: f.handover.date,
    insurance: f.extras.protect,
    packageImage: f.photo,
    ...senderIdentityPayload(senderForId(f, customer), customer as any),
    userId: customer.value,
    specifiedItems: storedItems(f),
    type: k.charAt(0).toUpperCase() + k.slice(1),
    creatorName,
    addOns: {
      phytosanitary: f.extras.phyto,
      bubbleWrap: f.extras.bubble,
      vacuumSeal: f.extras.vacuum,
    },
  }
}

const filledAddress = (l1?: string, l2?: string, city?: string, state?: string, pc?: string) => ({
  l1: l1 || '',
  l2: l2 || '',
  city: city || '',
  state: state || '',
  pc: pc || '',
})

const splitName = (full = '') => {
  const [first = '', ...rest] = String(full).trim().split(/\s+/)
  return { first, last: rest.join(' ') }
}

/** A draft, a Quick Quote prefill, or the old form state — all share the legacy field names. */
export function formFromLegacy(d: any, kind: Kind): BookingForm {
  const f = blankForm(kind)
  const typeRaw = String(d.shipment_type || '').toLowerCase()
  f.from = d.country || f.from
  f.to = d.receiverCountry || f.to
  if (kind === 'domestic') {
    f.fromState = d.state || ''
    f.toState = d.delivery_state || ''
  }
  f.type = typeRaw === 'document' && kind !== 'domestic' ? 'document' : 'package'
  if (Array.isArray(d.packages) && d.packages.length) {
    f.parcels = d.packages.map((p: any) => ({
      w: p.weight ? String(p.weight) : '',
      l: String(p.dimensions?.length ?? p.length ?? ''),
      wd: String(p.dimensions?.width ?? p.width ?? ''),
      h: String(p.dimensions?.height ?? p.height ?? ''),
      preset: 'custom',
    }))
  }
  f.contents = d.description || ''
  f.value = d.value && f.type === 'package' ? String(d.value) : ''
  f.purpose = d.purpose || ''
  f.photo = d.packageImage || ''

  const party = (side: Who): Party => {
    const s = side === 'sender'
    const first = s ? d.sender_first_name : d.receiver_first_name
    const last = s ? d.sender_last_name : d.receiver_last_name
    const addr = s
      ? filledAddress(d.pickup_address, d.pickup_address2, d.city, d.state, d.postcode)
      : filledAddress(d.delivery_address, d.delivery_address2, d.delivery_city, d.delivery_state, d.Deliverypostcode)
    const phone = s ? d.pickup_number : d.delivery_number
    const any = first || last || addr.l1
    return {
      ...f[side],
      mode: any ? 'other' : '',
      first: first || '',
      last: last || '',
      // Older drafts may have no company; the person's name is the usual stand-in
      company: (s ? d.sender_company : d.receiver_company) || [first, last].filter(Boolean).join(' '),
      phone: phone ? cleanPhone(phone, s ? f.from : f.to) : '',
      email: (s ? d.email : d.delivery_email) || '',
      addr,
      addrMode: 'manual',
      idImage: s ? d.senderIdImage || '' : '',
      idPreview: s ? d.senderIdImageUrl || null : null,
    }
  }
  f.sender = party('sender')
  f.receiver = party('receiver')
  // The account-holder choice is only asked on one side. A draft keeps what the
  // customer said about the sender; the name/phone check still runs on top.
  const own = ownerSide(f)
  if (own === 'sender' && f.sender.mode) f.sender.mode = d.senderIsAccountOwner === false ? 'other' : 'owner'
  if (own !== 'sender') f.sender.mode = ''
  if (own !== 'receiver') f.receiver.mode = ''

  if (d.pickup) f.handover = { mode: 'pickup', date: d.pickup_date || '' }
  f.extras.protect = !!d.insurance
  if (d.addOns && !Array.isArray(d.addOns)) {
    f.extras.phyto = !!d.addOns.phytosanitary
    f.extras.bubble = num(d.addOns.bubbleWrap)
    f.extras.vacuum = num(d.addOns.vacuumSeal)
  }

  if (Array.isArray(d.specifiedItems) && d.specifiedItems.length && f.type === 'package' && kind !== 'domestic') {
    f.items = d.specifiedItems.map((it: any) => {
      const code = it.commodityCodes?.value && it.commodityCodes.value !== 'nil' ? it.commodityCodes : null
      return {
        ...blankItem(),
        desc: it.description || it.itemName || '',
        qty: String(it.itemQuantity ?? '1'),
        price: it.itemValue ? String(it.itemValue) : '',
        weight: it.grossValue ? String(it.grossValue) : '',
        code: code ? { value: String(code.value), label: String(code.label || '') } : null,
        opts: code ? [{ value: String(code.value), label: String(code.label || '') }] : [],
        hs: code ? 'done' : 'idle',
        pid: {
          merchant: it.productIdentifiers?.merchant || '',
          manufacturer: it.productIdentifiers?.manufacturer || '',
          standardisedManufacturer: it.productIdentifiers?.standardisedManufacturer || '',
        },
      }
    })
  }
  return f
}

/** A booking from admin/booking/:id, for "Duplicate booking". */
export function formFromBooking(b: any, kind: Kind): BookingForm {
  const info = Array.isArray(b.delivery_info) ? b.delivery_info : []
  const snd = info.find((x: any) => x.type === 'CUSTOMER') || {}
  const rcv = info.find((x: any) => x.type === 'RECIEVER') || {}
  const sn = splitName(snd.contactInformation?.fullName)
  const rn = splitName(rcv.contactInformation?.fullName)
  return formFromLegacy(
    {
      shipment_type: b.shipment_type,
      packages: b.packages,
      description: b.description,
      value: b.value,
      country: snd.postalAddress?.countryCode,
      receiverCountry: rcv.postalAddress?.countryCode,
      sender_first_name: sn.first,
      sender_last_name: sn.last,
      sender_company: snd.contactInformation?.companyName,
      email: snd.contactInformation?.email,
      pickup_number: snd.contactInformation?.phone,
      pickup_address: snd.postalAddress?.addressLine1,
      pickup_address2: snd.postalAddress?.addressLine2,
      city: snd.postalAddress?.cityName,
      // Domestic stores the state in countyName; elsewhere it holds the country name
      state: kind === 'domestic' ? snd.postalAddress?.countyName : '',
      postcode: snd.postalAddress?.postalCode,
      receiver_first_name: rn.first,
      receiver_last_name: rn.last,
      receiver_company: rcv.contactInformation?.companyName,
      delivery_email: rcv.contactInformation?.email,
      delivery_number: rcv.contactInformation?.phone,
      delivery_address: rcv.postalAddress?.addressLine1,
      delivery_address2: rcv.postalAddress?.addressLine2,
      delivery_city: rcv.postalAddress?.cityName,
      delivery_state: kind === 'domestic' ? rcv.postalAddress?.countyName : '',
      Deliverypostcode: rcv.postalAddress?.postalCode,
      specifiedItems: b.specifiedItems,
      packageImage: b.packageImage,
      senderIsAccountOwner: b.senderIsAccountOwner,
    },
    kind,
  )
}
